import { useState } from 'react'
import styles from './SoundGuysReview.module.css'
import { useCatalogEvent } from 'lib/analytics/catalog'
import type { SoundGuysReviewModel } from 'lib/dealhunt/review'

interface SoundGuysReviewProps {
  review: SoundGuysReviewModel
}

/** How many of each list the condensed "Liked / Could be better" lines carry before "See all". */
const CONDENSED_POINTS = 4

/** "…ridiculously bassy., No audio" — a point written as a sentence loses its full stop in the line. */
function condensedLine(points: string[]): string {
  return points
    .slice(0, CONDENSED_POINTS)
    .map(p => p.trim().replace(/\.$/, ''))
    .join(', ')
}

/**
 * SoundGuys editorial review — the body of the verdict block (design 2a, 2026-09-22): the
 * rating card beside the verdict, points and CTA, then the category scores across the full
 * width (the scores left the card 2026-09-23 so a six-bar card no longer strands the right
 * column above a hole). Every field is real SoundGuys enrichment or it is not rendered at all — a missing verdict,
 * byline or pros/cons collapses its own sub-block rather than showing substitute copy. This
 * column carries a SoundGuys wordmark and byline, so anything inside it is a statement
 * SoundGuys is making; invented copy here would be a false editorial claim, and it would
 * also flow into the page's Review structured data.
 */
const SoundGuysReview: React.VFC<SoundGuysReviewProps> = ({ review }) => {
  const { reviewers, reviewDate, reviewUrl, summary, pros, cons } = review
  const { track } = useCatalogEvent()
  const [allPoints, setAllPoints] = useState(false)
  const onReviewClick = (location: string): void =>
    track('product_page_review_click', { location, destination_url: reviewUrl })
  const positions = reviewers
    .map(r => r.position)
    .filter((p): p is string => Boolean(p))
    .join(' · ')
  const pointCount = pros.length + cons.length
  const condensed = pros.length > CONDENSED_POINTS || cons.length > CONDENSED_POINTS

  return (
    <div className={styles.column}>
      <div className={styles.ratingCard}>
        <span className={styles.scoreEyebrow}>
          <span className={styles.wordmark}>
            <span className={styles.sound}>{'SOUND'}</span>
            <span className={styles.guys}>{'GUYS'}</span>
          </span>
          {review.promoted && review.score === null ? ' article' : ' rating'}
        </span>
        {review.score !== null && (
          <div className={styles.scoreValue}>
            <span className={styles.scoreNum}>{review.score.toFixed(1)}</span>
            <span className={styles.scoreScale}>{'/ 10'}</span>
          </div>
        )}
        {review.verdictLabel && (
          <span className={styles.verdict}>
            <span className={styles.verdictDot} aria-hidden="true" />
            {review.verdictLabel}
          </span>
        )}
        {review.promoted && (
          <span
            className={styles.promoted}
            title={review.sponsorName ? `Brought to you by ${review.sponsorName}` : undefined}
          >
            {'Promoted'}
          </span>
        )}
        {reviewers.length > 0 && (
          <div className={styles.reviewer}>
            <div className={styles.avatars}>
              {reviewers.map((r, i) => {
                const cls = `${styles.avatar} ${i % 2 ? styles.avatarAlt : ''}`
                return r.avatarUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img key={r.name} src={r.avatarUrl} alt={r.name} className={cls} />
                ) : (
                  <span key={r.name} className={cls}>
                    {r.initials}
                  </span>
                )
              })}
            </div>
            <span className={styles.reviewerText}>
              <span className={styles.reviewerEyebrow}>
                {review.promoted ? 'Written by' : 'Reviewed by'}
              </span>
              <span className={styles.reviewerName}>
                {reviewers.flatMap((r, i) => {
                  const name = r.profileUrl ? (
                    <a key={r.name} href={r.profileUrl} target="_blank" rel="noopener noreferrer">
                      {r.name}
                    </a>
                  ) : (
                    <span key={r.name}>{r.name}</span>
                  )
                  if (i === 0) {
                    return [name]
                  }
                  return [i === reviewers.length - 1 ? ' & ' : ', ', name]
                })}
              </span>
              {positions && <span className={styles.reviewerRole}>{positions}</span>}
              {reviewDate && <span className={styles.reviewerDate}>{reviewDate}</span>}
            </span>
          </div>
        )}
      </div>

      {review.subScores.length > 0 && (
        <div className={styles.stats}>
          {review.subScores.map(stat => (
            <div key={stat.key} className={styles.stat}>
              <div className={styles.statHead}>
                <span className={styles.statLabel}>{stat.label}</span>
                <span className={styles.statScore}>{stat.score.toFixed(1)}</span>
              </div>
              <div className={styles.bar}>
                <div className={styles.barFill} style={{ width: `${stat.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {summary && (
        <div className={styles.verdictBlock}>
          <p className={styles.verdictEyebrow}>{'The verdict'}</p>
          <p className={styles.summary}>{summary}</p>
        </div>
      )}

      {pointCount > 0 && (
        <div className={styles.points}>
          {allPoints ? (
            <>
              {pros.length > 0 && (
                <ul className={styles.pointList} aria-label="What we like">
                  {pros.map(p => (
                    <li key={p} className={styles.point}>
                      <span className={`${styles.pointIcon} ${styles.likeIcon}`}>{'✓'}</span>
                      <span className={styles.pointText}>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              {cons.length > 0 && (
                <ul className={styles.pointList} aria-label="What could be better">
                  {cons.map(c => (
                    <li key={c} className={styles.point}>
                      <span className={`${styles.pointIcon} ${styles.dislikeIcon}`}>{'✕'}</span>
                      <span className={styles.pointText}>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              {pros.length > 0 && (
                <p className={styles.pointLine}>
                  <span className={`${styles.pointIcon} ${styles.likeIcon}`}>{'✓'}</span>
                  <span className={styles.pointText}>
                    <span className={styles.likeTitle}>{'Liked: '}</span>
                    {condensedLine(pros)}
                  </span>
                </p>
              )}
              {cons.length > 0 && (
                <p className={styles.pointLine}>
                  <span className={`${styles.pointIcon} ${styles.dislikeIcon}`}>{'✕'}</span>
                  <span className={styles.pointText}>
                    <span className={styles.dislikeTitle}>{'Could be better: '}</span>
                    {condensedLine(cons)}
                  </span>
                </p>
              )}
            </>
          )}
          {(condensed || allPoints) && (
            <button
              type="button"
              className={styles.pointsToggle}
              aria-expanded={allPoints}
              onClick={() => setAllPoints(v => !v)}
            >
              {allPoints ? 'Show fewer points ⌃' : `See all ${pointCount} points ⌄`}
            </button>
          )}
        </div>
      )}

      {reviewUrl && (
        <a
          className={styles.ctaBar}
          href={reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onReviewClick('review_card')}
        >
          {review.promoted
            ? 'Read the full SoundGuys article →'
            : 'Read the full SoundGuys review →'}
        </a>
      )}
    </div>
  )
}

export default SoundGuysReview
